const dayKey = (d) => d.toISOString().slice(0, 10);

/** Executive dashboard KPI strip (spec values) */
export const executiveDashboardKPIs = {
  totalTenants: 48,
  activeTenants: 42,
  meetingsThisMonth: 186420,
  aiSummaries: 72900,
  failedJobs: 28,
  mrrUsd: 142000,
  mrrChangePct: 3.1,
  meetingsLast24h: 12840,
  aiTokensLast24hM: 42.7,
  failedJobsLastHour: 14,
  openCriticalAlerts: 5,
};

export const meetingsByDay = Array.from({ length: 30 }, (_, i) => {
  const d = new Date();
  d.setDate(d.getDate() - (29 - i));
  return {
    date: dayKey(d),
    count: 320 + ((i * 17) % 180) + (i * 3) % 37,
    uniqueTenants: 80 + (i % 45),
  };
});

export const planMixData = [
  { plan: 'Starter', count: 18, pct: 37.5 },
  { plan: 'Growth', count: 16, pct: 33.3 },
  { plan: 'Enterprise', count: 14, pct: 29.2 },
];

/** 4 connectors for executive strip: Microsoft, Google, WhatsApp, OpenAI */
export const connectorHealthStrip = [
  { id: 'microsoft', name: 'Microsoft', providerKey: 'microsoft', status: 'healthy', latencyMs: 112, errorRatePct: 0.02 },
  { id: 'google', name: 'Google', providerKey: 'google', status: 'healthy', latencyMs: 118, errorRatePct: 0.04 },
  { id: 'whatsapp', name: 'WhatsApp', providerKey: 'whatsapp', status: 'degraded', latencyMs: 240, errorRatePct: 0.8 },
  { id: 'openai', name: 'OpenAI', providerKey: 'openai', status: 'healthy', latencyMs: 890, errorRatePct: 0.12 },
];

const topMeetings = [12400, 10800, 9600, 8200, 7100, 6400, 5800, 5200, 4800, 4100];
const sumTop = topMeetings.reduce((a, b) => a + b, 0);

export const topTenantsByUsage = Array.from({ length: 10 }, (_, i) => ({
  tenantId: `tnt-${2000 + i}`,
  tenantCode: `MH-${String(i + 1).padStart(4, '0')}`,
  companyName: `High Volume Co ${i + 1}`,
  meetingsCount: topMeetings[i],
  meetings7d: topMeetings[i],
  pctOfTotal: Math.round((topMeetings[i] / sumTop) * 1000) / 10,
  rank: i + 1,
}));

export const expiringSubscriptions = Array.from({ length: 6 }, (_, i) => {
  const daysRemaining = [45, 28, 12, 8, 5, 22][i];
  return {
    id: `exp-${i}`,
    tenantCode: `MH-${String(5000 + i).padStart(4, '0')}`,
    companyName: `Renewal Pending ${i + 1}`,
    plan: i % 2 ? 'Growth' : 'Enterprise',
    renewsAt: new Date(Date.now() + daysRemaining * 86400000).toISOString(),
    daysRemaining,
    amountUsd: 1200 + i * 400,
  };
});

export const criticalAlerts = Array.from({ length: 5 }, (_, i) => ({
  id: `crt-${i}`,
  severity: 'CRITICAL',
  title: ['Webhook delivery storm', 'Queue backlog', 'Auth spike', 'Quota breach', 'Region latency'][i],
  tenantCode: i < 3 ? `MH-${String(1000 + i).padStart(4, '0')}` : null,
  openedAt: new Date(Date.now() - (i + 1) * 7200000).toISOString(),
  status: 'open',
}));

export const operationsCockpitData = {
  regions: [
    { code: 'eu-west-1', cpuPct: 62, healthy: true, tenantsAffected: 0 },
    { code: 'me-south-1', cpuPct: 48, healthy: true, tenantsAffected: 2 },
    { code: 'us-east-1', cpuPct: 71, healthy: false, tenantsAffected: 5 },
  ],
  deployVersion: '2025.03.19-14',
  lastIncident: '2025-03-10T08:00:00Z',
};

/** Provider chips for cockpit top band */
export const cockpitProviderStatus = [
  { id: 'ms', name: 'Microsoft', status: 'healthy' },
  { id: 'gg', name: 'Google', status: 'degraded' },
  { id: 'wa', name: 'WhatsApp', status: 'healthy' },
  { id: 'ai', name: 'OpenAI', status: 'incident' },
];

/** 2x2 queue widgets */
export const cockpitQueues = [
  { name: 'attendee-sync', depth: 842, throughputPerMin: 120, failureCount: 3, status: 'ok', utilizationPct: 62 },
  { name: 'meeting-sync', depth: 410, throughputPerMin: 88, failureCount: 0, status: 'ok', utilizationPct: 44 },
  { name: 'transcript', depth: 128, throughputPerMin: 24, failureCount: 7, status: 'warn', utilizationPct: 81 },
  { name: 'notifications', depth: 56, throughputPerMin: 200, failureCount: 1, status: 'ok', utilizationPct: 35 },
];

export const cockpitIncidentTimeline = [
  { id: 'ev-1', ts: new Date(Date.now() - 3600000).toISOString(), message: 'OpenAI latency spike detected (me-south-1)' },
  { id: 'ev-2', ts: new Date(Date.now() - 7200000).toISOString(), message: 'Google webhook validation retries elevated' },
  { id: 'ev-3', ts: new Date(Date.now() - 14400000).toISOString(), message: 'Transcript queue depth crossed warning threshold' },
  { id: 'ev-4', ts: new Date(Date.now() - 86400000).toISOString(), message: 'Scheduled maintenance: eu-west-1 — completed' },
];

export const cockpitWebhookHourly = {
  count4xx: 42,
  count5xx: 11,
  windowLabel: 'last hour',
};

export const cockpitSupportEscalations = [
  { ticket: 'T-13042', tenant: 'MH-0007', issue: 'WhatsApp template rejection', owner: 'agent2@mh.ai', status: 'in_progress' },
  { ticket: 'T-13039', tenant: 'MH-0012', issue: 'Calendar sync drift', owner: 'agent5@mh.ai', status: 'waiting' },
  { ticket: 'T-13028', tenant: 'MH-0003', issue: 'MOM approval loop', owner: 'agent1@mh.ai', status: 'resolved' },
];

export const queueStats = {
  transcription: { pending: 128, processing: 14, dlq: 2 },
  notifications: { pending: 56, processing: 8, dlq: 0 },
  webhooks: { pending: 22, processing: 3, dlq: 1 },
  exports: { pending: 9, processing: 2, dlq: 0 },
};

const subs = ['Microsoft', 'Google', 'WhatsApp', 'Jobs', 'Billing', 'integrations', 'security'];

export const alertsList = Array.from({ length: 24 }, (_, i) => {
  const sev = ['critical', 'high', 'medium', 'low'][i % 4];
  const muted = i === 5 || i === 11;
  return {
    id: `alt-${i}`,
    time: new Date(Date.now() - i * 5400000).toISOString(),
    severity: sev,
    subsystem: subs[i % subs.length],
    source: subs[i % subs.length],
    message: `Operational event ${i + 1}: synthetic monitoring — subsystem check`,
    impact: i % 3 === 0 ? 'Multi-tenant' : i % 3 === 1 ? 'Single tenant' : 'Degraded UX',
    owner: i % 4 === 0 ? 'unassigned' : `oncall${(i % 5) + 1}@mh.ai`,
    acknowledged: i % 7 === 0,
    muted,
    status: i % 7 === 0 ? 'acknowledged' : 'open',
  };
});
